<template>
  <div class="output">
    <n-header/>
    <article class="visualization">
      <section class="crop">
        <canvas class="crop-canvas" ref="canvas"></canvas>
        <div class="crop-data">
          <header>
            <h2>{{$t(`crops.${cropCode}`)}}</h2>
            <h3>{{currentDate}}</h3>
          </header>
          <div class="main">
            <div class="column">
              <!-- TODO: Put relevant info -->
            </div>
            <div class="column">
              <div class="data-element">
                <div class="label">BBCH</div>
                <div class="value">{{Math.round(bbch)}}</div>
              </div>
              <button @click="handleCropDataExportClick">
                {{$t('editor.export')}}
              </button>
            </div>
          </div>
          <footer>
            <div class="data-element">
              <!-- TODO: -->
            </div>
          </footer>
        </div>
      </section>
      <section class="parameters">
        <header>
          <nav class="tab-menu">
            <a
              key="tab-bones"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-bones')}"
              @click.prevent="handleTabClick('tab-bones')">
              {{$t('editor.tabs.bones')}}
            </a>
            <a
              key="tab-roots"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-roots')}"
              @click.prevent="handleTabClick('tab-roots')">
              {{$t('editor.tabs.roots')}}
            </a>
            <a
              key="tab-images"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-images')}"
              @click.prevent="handleTabClick('tab-images')">
              {{$t('editor.tabs.images')}}
            </a>
            <a
              key="tab-debug"
              class="tab-button"
              :class="{ 'selected': isTabSelected('tab-debug')}"
              @click.prevent="handleTabClick('tab-debug')">
              {{$t('editor.tabs.debug')}}
            </a>
          </nav>
        </header>
        <!-- Bones -->
        <div class="content" v-if="isTabSelected('tab-bones')">
          <n-bone-list />
          <div class="selection" v-if="selection.bones">
            <n-bone-properties  v-for="index in selection.bones" :key="`selection-bone-${index}`" :index="index" />
          </div>
        </div>
        <!-- /Bones -->
        <!-- Images -->
        <div class="content" v-if="isTabSelected('tab-images')">
          <n-images />
        </div>
        <!-- /Images -->
        <!-- Roots -->
        <div class="content" v-if="isTabSelected('tab-roots')">
          <n-root-list />
          <div class="selection" v-if="selection.roots">
            <n-root-properties v-for="index in selection.roots" :key="`selection-root-${index}`" :index="index" />
          </div>
        </div>
        <!-- /Roots -->
        <!-- Debug -->
        <div class="content" v-if="isTabSelected('tab-debug')">
          <n-debug />
        </div>
        <!-- /Debug -->
        <footer>

        </footer>
      </section>
      <n-tooltip v-if="tooltipShown"></n-tooltip>
      <n-control-bar
        :start="start"
        :end="end"
        :current="current"
        :buffered="buffered"
        :is-buffering="isBuffering"
        :is-playing="isPlaying"
        custom-class="control-bar"
        @over="handleControlBarOver"
        @out="handleControlBarOut"
        @seek="handleControlBarSeek"
        @play="handleControlBarPlay"
        @pause="handleControlBarPause" />
    </article>
  </div>
</template>

<style lang="scss" scoped>

.visualization {
  height: calc(100vh - 4rem);
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 4rem;
  grid-template-areas:
    "crop parameters"
    "control-bar control-bar"
}

.crop {
  grid-area: crop;
  position: relative;
  display: flex;
  padding: 0 $spacing--m;
}

.crop-canvas {
  position: absolute;
  width: calc(100% - 4rem);
  height: 100%;
  z-index: -1;
}

.crop-data {
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-top: $spacing--s;
  }
  .main {
    flex: 1;
    display: flex;
    justify-content: space-between;
  }
  .column:last-child {
    text-align: right;
  }
}

.graph {
  margin-bottom: $spacing--l;
}

.data-element {
  margin-bottom: $spacing--s;
  display: grid;
  grid-template-areas:
    "icon label"
    "icon value";
  grid-template-columns: $spacing--m auto;
  .icon {
    color: var(--color--secondary);
    grid-area: icon;
    margin-top: 0.25rem;
  }
  .label {
    @extend %text-style--label;
    font-size: var(--text--xs);
    color: var(--color--secondary);
    grid-area: label;
  }
  .value {
    grid-area: value;
  }
}

.parameters {
  grid-area: parameters;
  border-left: 1px solid var(--color--outlines);
  overflow: auto;
  header {
    height: var(--height--header);
    box-shadow: 0 1px 0 0 var(--color--outlines);
    display: flex;
    justify-content: center;
    align-items: flex-end;
  }
  .content {
    padding: $spacing--m;
    height: calc(100% - var(--height--header));
    overflow-y: auto;
  }
  footer {
    display: flex;
    padding: $spacing--m;
    justify-content: flex-end;
  }
}

.control-bar {
  grid-area: control-bar;
  border-top: 1px solid var(--color--outlines);
}

.button--remove-application {
  height: $spacing--l;
  width: $spacing--l;
  color: var(--color--secondary);
  padding: 0;
  display: flex;
  align-items: center;
  justify-items: center;
  background-color: transparent;
  margin-bottom: 0.125rem;
}

</style>

<script>
import moment from 'moment'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import Header from '@/components/Header'
import ControlBar from '@/components/ControlBar'
import Tooltip from '@/components/Tooltip'
import BoneList from '@/components/editor/BoneList'
import BoneProperties from '@/components/editor/BoneProperties'
import RootList from '@/components/editor/RootList'
import RootProperties from '@/components/editor/RootProperties'
import Images from '@/components/editor/Images'
import Debug from '../components/editor/Debug'

import simulation from '@/simulation'
import { isInside, getCenteredCoordinates } from '@/helpers/canvas'

let currentSimulation

export default {
  name: 'output-view',
  components: {
    'n-header': Header,
    'n-control-bar': ControlBar,
    'n-tooltip': Tooltip,
    'n-bone-list': BoneList,
    'n-bone-properties': BoneProperties,
    'n-root-list': RootList,
    'n-root-properties': RootProperties,
    'n-images': Images,
    'n-debug': Debug
  },
  data () {
    return {
      tabSelected: 'tab-bones',
      isCreating: false,
      isDragging: false,
      magnetRadius: 20
    }
  },
  computed: {
    ...mapGetters({
      days: 'simulation/days',
      start: 'simulation/start',
      end: 'simulation/end',
      current: 'simulation/current',
      data: 'simulation/data',
      buffered: 'simulation/buffered',
      progress: 'simulation/progress'
    }),
    ...mapState({
      buffer: (state) => state.simulation.buffer,
      bufferedMin: (state) => state.simulation.bufferedMin,
      bufferedMax: (state) => state.simulation.bufferedMax,
      currentDate: (state) => moment(state.simulation.currentDate).format('DD-MM-YYYY'),
      region: (state) => state.region.type,
      regionName: (state) => state.region.type && state.region.type.name,
      soil: (state) => state.soil.type,
      soilName: (state) => state.soil.type && state.soil.type.name,
      crop: (state) => state.crop.type,
      cropCode: (state) => state.crop.type && state.crop.type.code,
      cropName: (state) => state.crop.type && state.crop.type.name,
      cropData: (state) => state.crop.data,
      cropDataVersion: (state) => state.crop.version,
      selection: (state) => state.editor.selection,
      hasEnded: (state) => state.simulation.hasEnded,
      hasStarted: (state) => state.simulation.hasStarted,
      isPlaying: (state) => state.simulation.isPlaying,
      isBuffering: (state) => state.simulation.isBuffering,
      bbch: (_, getters) => getters['simulation/data'] && getters['simulation/data'].BBCH,
      tooltipShown: (state) => state.tooltip.shown
    })
  },
  watch: {
    isPlaying (value) {
      if (currentSimulation && value === false) {
        currentSimulation.pause()
      } else if (currentSimulation && value === true) {
        currentSimulation.resume()
      }
    },
    cropDataVersion (value) {
      if (currentSimulation) {
        currentSimulation.setCropData(this.cropData)
      }
    },
    cropData (value) {
      if (currentSimulation && value) {
        currentSimulation.setCropData(value)
      }
    },
    data (value, oldValue) {
      if (currentSimulation && value && oldValue) {
        currentSimulation.seek(value, value && oldValue && value.Date !== oldValue.Date)
      }
    }
  },
  methods: {
    ...mapMutations({
      createBoneStartAt: 'crop/createBoneStartAt',
      updateLastBoneEnd: 'crop/updateLastBoneEnd',
      tooltipShow: 'tooltip/show',
      tooltipHide: 'tooltip/hide'
    }),
    ...mapActions({
      seek: 'simulation/seek',
      play: 'simulation/play',
      pause: 'simulation/pause',
      loadCrop: 'crop/loadData',
      saveCrop: 'crop/saveData',
      movePointsBy: 'editor/movePointsBy',
      selectNearestPointOrDeselectAt: 'editor/selectNearestPointOrDeselectAt',
      select: 'editor/select',
      deselect: 'editor/deselect'
    }),
    /* End of chart functions */
    isTabSelected (item) {
      return this.tabSelected === item
    },
    handleTabClick (item) {
      this.tabSelected = item
    },
    handleControlBarOut () {
      this.tooltipHide()
    },
    handleControlBarOver ({ time, x, y }) {
      const color = getComputedStyle(document.documentElement).getPropertyValue('--color--primary')
      this.tooltipShow({
        text: moment(time).format('DD-MM-YYYY'),
        x,
        y,
        color: color
      })
    },
    handleControlBarSeek (time) {
      this.seek(time)
    },
    handleControlBarPlay () {
      this.play()
    },
    handleControlBarPause () {
      this.pause()
    },
    handleCropDataExportClick (e) {
      this.saveCrop()
    },
    handleCanvasDoubleClick (e) {
      if (isInside(this.$refs.canvas, e)) {
        const position = getCenteredCoordinates(this.$refs.canvas, e)
        this.createBoneStartAt({ position })
        this.isCreating = true
      }
    },
    handleCanvasMouseDown (e) {
      if (isInside(this.$refs.canvas, e)) {
        if (this.isCreating) {
          if (e.shiftKey) {
            const position = getCenteredCoordinates(this.$refs.canvas, e)
            this.createBoneStartAt({ position, useLastParent: true })
          } else {
            this.isCreating = false
          }
        } else {
          this.isDragging = true
          const position = getCenteredCoordinates(this.$refs.canvas, e)
          this.selectNearestPointOrDeselectAt({ position, radius: this.magnetRadius })
        }
      }
    },
    handleCanvasMouseMove (e) {
      if (isInside(this.$refs.canvas, e)) {
        if (currentSimulation) {
          currentSimulation.setCursorPosition(getCenteredCoordinates(this.$refs.canvas, e), this.magnetRadius)
        }
        const position = getCenteredCoordinates(this.$refs.canvas, e)
        if (this.isCreating) {
          this.updateLastBoneEnd({ position })
        } else if (this.isDragging) {
          const move = [e.movementX, e.movementY]
          this.movePointsBy({ move })
        }
      }
    },
    handleCanvasMouseUp (e) {
      if (this.isDragging) {
        this.isDragging = false
        this.deselect({ name: 'points' })
      }
    }
  },
  created () {
    if (!this.crop && !this.hasStarted) {
      this.$router.replace('/input')
    }
  },
  async mounted () {
    window.addEventListener('dblclick', this.handleCanvasDoubleClick)
    window.addEventListener('mousedown', this.handleCanvasMouseDown)
    window.addEventListener('mousemove', this.handleCanvasMouseMove)
    window.addEventListener('mouseup', this.handleCanvasMouseUp)
    try {
      const { canvas } = this.$refs
      currentSimulation = await simulation.init(canvas, await this.loadCrop(this.crop))
      if (currentSimulation) {
        currentSimulation.start(this.data)
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error(error)
    }
  },
  beforeDestroy () {
    window.removeEventListener('dblclick', this.handleCanvasDoubleClick)
    window.removeEventListener('mousedown', this.handleCanvasMouseDown)
    window.removeEventListener('mousemove', this.handleCanvasMouseMove)
    window.removeEventListener('mouseup', this.handleCanvasMouseUp)
    if (currentSimulation) {
      currentSimulation.stop()
    }
    currentSimulation = undefined
  },
  beforeRouteLeave (to, from, next) {
    const answer = window.confirm(this.$t('editor.exit-warning'))
    if (answer) {
      return next()
    } else {
      return next(false)
    }
  }
}
</script>
